import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { absoluteTime } from "../../lib/time";
import type { CompanyDetail } from "../../lib/crm-types";

/**
 * What this company does, written by a model from public web results.
 *
 * The sources are as much the point as the text. A model asked about a small
 * electrical wholesaler nobody has heard of will produce three confident,
 * plausible, entirely invented sentences, and there is no way to tell that from
 * the prose -- so the pages it read are listed underneath, and a summary
 * generated without a web search says so out loud rather than passing as one
 * that had.
 *
 * Generated on demand, never on page load: it is a paid call about a fact that
 * changes roughly once a year.
 */
export function AiSummaryPanel({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();

  const generate = useMutation({
    mutationFn: () => crmApi.generateSummary(company.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(company.id),
      });
    },
  });

  const ungrounded = generate.data?.grounded === false;

  return (
    <section className="rounded-lg border border-line">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          What they do
        </h2>
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="ml-auto text-xs text-brand-600 hover:text-brand-700 disabled:opacity-50"
        >
          {generate.isPending
            ? "Looking…"
            : company.aiSummary
              ? "Refresh"
              : "Look it up"}
        </button>
      </header>

      <div className="px-3 py-2">
        {company.aiSummary ? (
          <>
            <p className="text-xs leading-relaxed text-content">
              {company.aiSummary}
            </p>

            {company.aiSummarySources?.length ? (
              <ul className="mt-2 flex flex-col gap-0.5">
                {company.aiSummarySources.map((source) => (
                  <li key={source.url}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-[11px] text-muted hover:text-brand-600"
                    >
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[11px] text-sla-warning">
                Written without a web search — check it before repeating it.
              </p>
            )}

            {company.aiSummaryGeneratedAt ? (
              <p className="mt-2 text-[11px] text-faint">
                Written {absoluteTime(company.aiSummaryGeneratedAt)} by AI.
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-faint">
            Nothing yet. Looking it up asks a model to search the web and
            summarise what this company does — useful thirty seconds before a
            call.
          </p>
        )}

        {ungrounded && !company.aiSummary ? (
          <p className="mt-2 text-[11px] text-sla-warning">
            No search-capable provider is configured, so this came from the
            model's own memory.
          </p>
        ) : null}

        {generate.isError ? (
          <p className="mt-2 text-[11px] text-sla-breached">
            {generate.error instanceof ApiError
              ? generate.error.message
              : "Could not generate a summary."}
          </p>
        ) : null}
      </div>
    </section>
  );
}
